import type { Cell, Rule } from '../protocol';

export const HIGHLIGHT_CLASS: Record<string, string> = {
  red: 'hl-red',
  yellow: 'hl-yellow',
  green: 'hl-green',
};

export function highlightClass(color?: string): string {
  return HIGHLIGHT_CLASS[(color || 'red').toLowerCase()] || 'hl-red';
}

function num(v: unknown): number | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v.replace(',', '.')))) return Number(v.replace(',', '.'));
  return null;
}

/** Numeric comparison when both sides are numbers, string comparison otherwise. */
export function test(op: Rule['op'], left: Cell, right: unknown): boolean {
  const ls = left === null || left === undefined ? '' : String(left);
  switch (op) {
    case 'empty':
      return ls.trim() === '';
    case '!empty':
      return ls.trim() !== '';
    case 'contains':
      return ls.toLowerCase().includes(String(right ?? '').toLowerCase());
    case '!contains':
      return !ls.toLowerCase().includes(String(right ?? '').toLowerCase());
  }

  const ln = num(left);
  const rn = num(right);
  if (ln !== null && rn !== null) {
    switch (op) {
      case '>': return ln > rn;
      case '>=': return ln >= rn;
      case '<': return ln < rn;
      case '<=': return ln <= rn;
      case '=': return ln === rn;
      case '!=': return ln !== rn;
    }
  }

  const rs = right === null || right === undefined ? '' : String(right);
  switch (op) {
    case '>': return ls > rs;
    case '>=': return ls >= rs;
    case '<': return ls < rs;
    case '<=': return ls <= rs;
    case '=': return ls === rs;
    case '!=': return ls !== rs;
  }
  return false;
}

/** Everything a table render decides beyond the raw data. */
export interface FormatResult {
  rowClass: Map<number, string>;
  cellClass: Map<string, string>; // `${row}:${col}`
  hiddenRows: Set<number>;
  hiddenCols: Set<string>; // lower-case column keys
  hideWidget: boolean;
}

export function newResult(): FormatResult {
  return { rowClass: new Map(), cellClass: new Map(), hiddenRows: new Set(), hiddenCols: new Set(), hideWidget: false };
}

/** The editor's declarative rules (widget.rules), applied to the data rows. */
export function applyRules(rules: Rule[] | undefined, keys: string[], rows: Cell[][], out: FormatResult): void {
  if (!rules || rules.length === 0) return;

  const index = new Map<string, number>();
  keys.forEach((k, i) => index.set(k.toLowerCase(), i));

  for (const rule of rules) {
    const ci = index.get((rule.column || '').toLowerCase());

    if (rule.action === 'hide-column' && ci !== undefined && rule.op === undefined) {
      out.hiddenCols.add(keys[ci].toLowerCase());
      continue;
    }

    if (rule.action === 'hide-widget' && (rule.op === 'empty' || !rule.column) && rows.length === 0) {
      out.hideWidget = true;
      continue;
    }

    if (ci === undefined) continue;
    const other = rule.compareColumn ? index.get(rule.compareColumn.toLowerCase()) : undefined;

    let anyMatch = false;
    rows.forEach((row, r) => {
      const right = other !== undefined ? row[other] : rule.value;
      if (!test(rule.op, row[ci], right)) return;
      anyMatch = true;

      switch (rule.action) {
        case 'row':
          out.rowClass.set(r, highlightClass(rule.color));
          break;
        case 'cell': {
          const target = rule.target ? index.get(rule.target.toLowerCase()) ?? ci : ci;
          out.cellClass.set(r + ':' + keys[target].toLowerCase(), highlightClass(rule.color));
          break;
        }
        case 'hide-row':
          out.hiddenRows.add(r);
          break;
      }
    });

    if (rule.action === 'hide-column' && anyMatch) out.hiddenCols.add(keys[ci].toLowerCase());
    if (rule.action === 'hide-widget' && anyMatch) out.hideWidget = true;
  }
}

/**
 * The old global formatter API, unchanged:
 *   function ZHPH_STATIONS(row) {
 *     if (row.instation > 20) row.highlight('red');
 *     row.cols.errorlevel.hide();
 *     if (row.table.rowCount == 0) row.table.hide();
 *   }
 * It ran once per <tr>, header included, with lower-cased column keys holding the cell text.
 */
export function applyLegacyFormatter(
  fn: (row: any) => void,
  keys: string[],
  labels: string[],
  rows: Cell[][],
  out: FormatResult,
  onError: (e: unknown) => void,
): void {
  const lower = keys.map((k) => k.toLowerCase());
  const rowCount = rows.length;

  const run = (r: number, values: (Cell | string)[], isHeader: boolean) => {
    const row: any = {
      cols: {} as Record<string, any>,
      table: {
        rowCount,
        hide: () => { out.hideWidget = true; },
      },
      highlight: (color?: string) => {
        if (!isHeader) out.rowClass.set(r, highlightClass(color));
      },
      hide: () => {
        if (!isHeader) out.hiddenRows.add(r);
      },
    };

    lower.forEach((k, i) => {
      const v = values[i];
      row[k] = v === null || v === undefined ? '' : String(v);
      row.cols[k] = {
        hide: () => out.hiddenCols.add(k),
        highlight: (color?: string) => {
          if (!isHeader) out.cellClass.set(r + ':' + k, highlightClass(color));
        },
        textContent: row[k],
        innerText: row[k],
      };
    });

    try {
      fn(row);
    } catch (e) {
      onError(e);
    }
  };

  run(-1, labels, true);
  rows.forEach((values, r) => run(r, values, false));
}

export function legacyFormatterFor(query: string | undefined): ((row: any) => void) | null {
  if (!query) return null;
  const fn = (window as any)[query];
  return typeof fn === 'function' ? fn : null;
}
